import {
    AuthContext,
    DataTable,
    DataTablePresenter,
    IAuthContextProps,
    ITableColumn,
    ITableFetchOptions,
    ProviderCollectionInvoice,
    Tooltip,
    renderDate,
} from '@enter/core';
import { mdiEyeOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, parseSortBy, RootStore } from 'stores';
import { amountColumnSize, BalanceDueCell, PatientNameCell, TableEmpty } from './patient-collections-cells';
import {
    collectionsSearchEmptyMessage,
    initialFiltersFromPatientIdSearch,
    syncPatientIdSearch,
} from './patient-collections-filters';
import * as S from './patient-collections.styles';

interface Props extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    onOpen: (invoiceId: string) => void;
    patientName: (first?: string, last?: string) => string;
    patientIdSearch?: string;
}

interface State {
    loading: boolean;
}

const filterSchemas = [];

function daysSince(dateStr?: string): number | undefined {
    if (!dateStr) return undefined;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return undefined;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
}

class PatientCollectionsOutstandingPage extends React.Component<Props, State> {
    private presenter: DataTablePresenter<ProviderCollectionInvoice>;
    private columns: ITableColumn<ProviderCollectionInvoice>[] = [];

    constructor(props: Props) {
        super(props);
        this.state = { loading: false };
        void (props.rootStore as RootStore);

        this.columns = [
            {
                id: 'Patient',
                Header: 'Patient',
                accessor: row => row,
                disableSortBy: true,
                Cell: ({ row }) => {
                    const inv = row.original;
                    const info = inv.invoicePatient?.info;
                    const id = inv.collectionInvoiceId;
                    return (
                        <PatientNameCell
                            name={props.patientName(info?.patientFirstName, info?.patientLastName)}
                            sub={[inv.controlNumber, inv.invoicePatient?.externalPatientId]
                                .filter(Boolean)
                                .join(' · ')}
                            onOpen={() => {
                                if (id) props.onOpen(id);
                            }}
                        />
                    );
                },
            },
            {
                id: 'Dos',
                Header: 'DOS',
                accessor: 'serviceDate',
                disableSortBy: true,
                Cell: ({ cell }) => <S.Cell>{renderDate(cell.value) || <S.Unknown>—</S.Unknown>}</S.Cell>,
            },
            {
                id: 'Payer',
                Header: 'Payer',
                accessor: 'primaryPayerName',
                disableSortBy: true,
                Cell: ({ cell }) => <S.Cell>{cell.value || <S.Unknown>—</S.Unknown>}</S.Cell>,
            },
            {
                id: 'Balance',
                Header: 'Balance Due',
                accessor: 'currentBalance',
                disableSortBy: true,
                ...amountColumnSize,
                Cell: ({ cell }) => <BalanceDueCell amount={cell.value} />,
            },
            {
                id: 'Billed',
                Header: 'Billed',
                accessor: 'postDate',
                disableSortBy: true,
                Cell: ({ cell }) => <S.Cell>{renderDate(cell.value) || <S.Unknown>—</S.Unknown>}</S.Cell>,
            },
            {
                id: 'Outstanding',
                Header: 'Outstanding',
                accessor: 'postDate',
                disableSortBy: true,
                Cell: ({ cell }) => {
                    const days = daysSince(cell.value);
                    if (days == null) return <S.Cell><S.Unknown>—</S.Unknown></S.Cell>;
                    const tone = days >= 30 ? 'bad' : days >= 14 ? 'warn' : 'ok';
                    return (
                        <S.Cell>
                            <S.DaysChip $tone={tone}>{days}d</S.DaysChip>
                        </S.Cell>
                    );
                },
            },
            {
                id: 'View',
                Header: '',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <S.ActionCell>
                        <Tooltip direction="top" title={<span>View statement</span>}>
                            <S.IconActionButton
                                variant="outlined"
                                aria-label="View statement"
                                onClick={e => {
                                    e.stopPropagation();
                                    const id = row.original.collectionInvoiceId;
                                    if (id) props.onOpen(id);
                                }}>
                                <Icon path={mdiEyeOutline} size="18px" />
                            </S.IconActionButton>
                        </Tooltip>
                    </S.ActionCell>
                ),
            },
        ];

        this.presenter = new DataTablePresenter<ProviderCollectionInvoice>({
            name: 'patient-collections-outstanding',
            data: [],
            columns: this.columns,
            filterSchemas,
            filters: initialFiltersFromPatientIdSearch(props.patientIdSearch),
        });
    }

    public componentDidUpdate(prevProps: Props): void {
        syncPatientIdSearch(this.presenter, this.props.patientIdSearch, prevProps.patientIdSearch);
    }

    public componentWillUnmount(): void {
        this.props.authContext.cancelGroup?.('patient-collections-outstanding');
    }

    public render() {
        return (
            <S.TableShell>
                <DataTable
                    presenter={this.presenter}
                    isLoading={this.state.loading}
                    hideFiltering
                    NotFoundComponent={() => (
                        <TableEmpty
                            message={collectionsSearchEmptyMessage(
                                this.props.patientIdSearch,
                                'No outstanding invoices.',
                            )}
                        />
                    )}
                    onFetch={this.onFetch}
                    onRowClick={row => {
                        const id = row.original.collectionInvoiceId;
                        if (id) this.props.onOpen(id);
                    }}
                />
            </S.TableShell>
        );
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({ loading: true });
            this.props.authContext.cancelGroup?.('patient-collections-outstanding');
            this.props.authContext.setGroup?.('patient-collections-outstanding');
            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, this.columns);
            const listing = await this.props.rootStore.collectionInvoiceStore.loadInvoiceListing(
                {
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                },
                'outstanding',
            );
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
        } catch (err) {
            this.props.dialogContext.showError(err);
        } finally {
            this.setState({ loading: false });
        }
    };
}

export const PatientCollectionsOutstanding = inject('rootStore')(
    observer((props: Omit<Props, 'authContext' | 'dialogContext'> & IInjectedRootStore) => {
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return (
            <PatientCollectionsOutstandingPage
                {...props}
                authContext={authContext}
                dialogContext={dialogContext}
            />
        );
    }),
);
